import React, { useState, useMemo, useCallback } from 'react'
import { split, uniq } from 'lodash'
import { Card, Form, Row, Col, Dropdown } from 'react-bootstrap'
import Page from 'layout/access-management/page'
import PageTitle from 'layout/page-title'
import useWorkflowProcess from 'api/hooks/use-workflow-process'
import { useBusinessRules } from 'api/hooks/use-business-rules'
import { useDebounce } from 'api/hooks/use-debounce'
import BusinessRulesTable from './BusinessRulesTable'
import ProcessFlowchartTab from './ProcessFlowchartTab'
import Tabs from 'duties/shared/tabs'
import UserContext from 'context/user-context'
import { wfApprovalTypes } from './approvalTypes'

function WorkflowProcessShow() {
  const path = window.location.pathname
  const id = split(path, '/')[2]

  const { data: process, isSuccess } = useWorkflowProcess({ id })

  const [activeTab, setActiveTab] = useState('table')
  const [selectedDepartment, setSelectedDepartment] = useState('')
  const [selectedApprovalTypes, setSelectedApprovalTypes] = useState([])
  const [approvalTypeSearch, setApprovalTypeSearch] = useState('')
  const [amount, setAmount] = useState('')
  const debouncedAmount = useDebounce(amount, 500)

  const filteredApprovalTypes = useMemo(() => {
    if (!approvalTypeSearch) return wfApprovalTypes
    const searchLower = approvalTypeSearch.toLowerCase()
    return wfApprovalTypes.filter((opt) =>
      `${opt.value} - ${opt.label}`.toLowerCase().includes(searchLower)
    )
  }, [approvalTypeSearch])

  // Fetch business rules to get available departments
  const { data: rulesData, isSuccess: rulesSuccess } = useBusinessRules({
    params: {
      process_type_id: id,
      perPage: 1000
    }
  })

  // Get unique departments from the data
  const departments = useMemo(() => {
    if (!rulesSuccess) return []
    const depts = rulesData.data
      .map((r) => r.department)
      .filter((d) => d !== null && d !== '')
    return uniq(depts).sort()
  }, [rulesData, rulesSuccess])

  const departmentOptions = useMemo(() => {
    return [
      { value: '', label: 'All Departments' },
      ...departments.map((d) => ({ value: d, label: d }))
    ]
  }, [departments])

  const handleAmountChange = useCallback((e) => {
    setAmount(e.target.value)
  }, [])

  const hasActiveFilters = selectedDepartment !== '' || selectedApprovalTypes.length > 0 || amount !== ''

  const handleClearAllFilters = useCallback(() => {
    setSelectedDepartment('')
    setSelectedApprovalTypes([])
    setAmount('')
    setApprovalTypeSearch('')
  }, [])

  const businessRulesTable = useMemo(
    () => (
      <BusinessRulesTable
        processId={id}
        selectedDepartment={selectedDepartment}
        selectedApprovalTypes={selectedApprovalTypes}
        amount={debouncedAmount}
      />
    ),
    [id, selectedDepartment, selectedApprovalTypes, debouncedAmount]
  )

  const processFlowchartTab = useMemo(
    () => (
      <ProcessFlowchartTab
        processId={id}
        selectedDepartment={selectedDepartment}
        selectedApprovalTypes={selectedApprovalTypes}
        amount={debouncedAmount}
      />
    ),
    [id, selectedDepartment, selectedApprovalTypes, debouncedAmount]
  )

  const crumbs = [
    { name: 'Workflow Processes', url: 'processes' },
    { name: isSuccess ? process.name : '', url: '' }
  ]

  return (
    <Page pageTitle={`Workflow Process: ${isSuccess ? process.name : ''}`}>
      <PageTitle breadcrumbs={crumbs} />
      <h4 className="mb-4">{isSuccess && process.name}</h4>

      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Department</Form.Label>
                <Form.Select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                >
                  {departmentOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Approval Type</Form.Label>
                <Dropdown autoClose="outside" onToggle={(isOpen) => !isOpen && setApprovalTypeSearch('')}>
                  <Dropdown.Toggle
                    as="button"
                    className="form-select text-start"
                  >
                    <span className={selectedApprovalTypes.length === 0 ? 'text-muted' : ''}>
                      {selectedApprovalTypes.length === 0
                        ? 'Select approval types...'
                        : `${selectedApprovalTypes.length} selected`}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu style={{ width: '100%' }}>
                    <div className="px-2 pb-2">
                      <div className="position-relative">
                        <Form.Control
                          type="text"
                          placeholder="Search..."
                          value={approvalTypeSearch}
                          onChange={(e) => setApprovalTypeSearch(e.target.value)}
                          size="sm"
                        />
                        {approvalTypeSearch && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm position-absolute end-0 top-50 translate-middle-y text-muted p-0 pe-2"
                            onClick={() => setApprovalTypeSearch('')}
                            style={{ textDecoration: 'none' }}
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {filteredApprovalTypes.map((option) => (
                        <Dropdown.ItemText key={option.value} className="py-1">
                          <Form.Check
                            type="checkbox"
                            id={`approval-type-${option.value}`}
                            label={`${option.value} - ${option.label}`}
                            checked={selectedApprovalTypes.includes(option.value)}
                            onChange={() => {
                              setSelectedApprovalTypes((prev) =>
                                prev.includes(option.value)
                                  ? prev.filter((v) => v !== option.value)
                                  : [...prev, option.value]
                              )
                            }}
                          />
                        </Dropdown.ItemText>
                      ))}
                      {filteredApprovalTypes.length === 0 && (
                        <Dropdown.ItemText className="text-muted small">
                          No results found
                        </Dropdown.ItemText>
                      )}
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={handleAmountChange}
                />
              </Form.Group>
            </Col>
            <Col md="auto">
              <Form.Group>
                <Form.Label className="invisible">Clear</Form.Label>
                <button
                  type="button"
                  className="form-control"
                  disabled={!hasActiveFilters}
                  onClick={handleClearAllFilters}
                  style={{ cursor: hasActiveFilters ? 'pointer' : 'not-allowed' }}
                >
                  Clear All
                </button>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-flex mb-2">
        <Tabs
          tabs={[
            { name: 'Table', value: 'table' },
            { name: 'Flowchart', value: 'flowchart' }
          ]}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>

      <div className="mt-4">
        {activeTab === 'table' && businessRulesTable}
        {activeTab === 'flowchart' && processFlowchartTab}
      </div>
    </Page>
  )
}

export default WorkflowProcessShow
